import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Smartphone, Share, PlusSquare, CheckCircle2, CalendarDays, MoreVertical, Download, Copy, Check } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LOGO_URL } from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export default function Welcome() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [installed, setInstalled] = useState(isStandalone());
  const [prompt, setPrompt] = useState(window.__ddInstallPrompt || null);
  const [tab, setTab] = useState(isIos() ? "iphone" : "android");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onReady = (e) => setPrompt(e.detail);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("dd-install-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("dd-install-ready", onReady); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const handleAdd = async () => {
    if (!prompt) { document.getElementById("install-guide")?.scrollIntoView({ behavior: "smooth", block: "start" }); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    window.__ddInstallPrompt = null;
    setPrompt(null);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/welcome`); setCopied(true); toast.success(t("welcome.link_copied")); setTimeout(() => setCopied(false), 2500); }
    catch { toast.error(t("welcome.copy_fail")); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-start sm:items-center justify-center px-4 py-10" data-testid="welcome-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <img src={LOGO_URL} alt="D&D Padel" className="w-24 h-24 rounded-3xl mx-auto shadow-2xl shadow-coral/20 mb-6" />
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("welcome.tagline")}</p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-3">{t("welcome.title")} <span className="text-golden">D&amp;D Padel</span></h1>
        <p className="text-muted-foreground mb-8">{t("welcome.subtitle")}</p>

        {installed ? (
          <div className="rounded-2xl border border-teal/30 bg-teal/5 p-5 mb-4" data-testid="welcome-installed">
            <CheckCircle2 size={28} className="text-teal mx-auto mb-2" />
            <p className="font-semibold">{t("welcome.installed")}</p>
          </div>
        ) : (
          <>
            <Button onClick={handleAdd} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white h-14 text-base font-semibold shadow-lg shadow-coral/25 gap-2 mb-3" data-testid="welcome-add-btn">
              <Smartphone size={18} /> {t("welcome.add")}
            </Button>

            <div id="install-guide" className="rounded-2xl border border-border/50 bg-card p-4 text-left mb-4" data-testid="welcome-guide">
              <div className="flex gap-1 bg-muted/40 rounded-full p-1 mb-4">
                {[["iphone", t("welcome.iphone")], ["android", t("welcome.android")]].map(([v, l]) => (
                  <button key={v} onClick={() => setTab(v)} className={`flex-1 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-coral text-white" : "text-muted-foreground"}`} data-testid={`guide-tab-${v}`}>{l}</button>
                ))}
              </div>

              {tab === "iphone" ? (
                <div className="space-y-4" data-testid="guide-iphone">
                  <div>
                    <p className="font-heading font-bold text-sm mb-2">{t("welcome.chrome_ios")}</p>
                    <Step n={1} icon={Share} text={t("welcome.ios1")} />
                    <Step n={2} icon={PlusSquare} text={t("welcome.ios2")} />
                    <Step n={3} icon={CheckCircle2} text={t("welcome.ios3")} />
                  </div>
                  <div className="pt-3 border-t border-border/40">
                    <p className="font-heading font-bold text-sm mb-2">{t("welcome.safari_ios")}</p>
                    <Step n={1} icon={Share} text={t("welcome.saf1")} />
                    <Step n={2} icon={PlusSquare} text={t("welcome.saf2")} />
                  </div>
                  <div className="pt-3 border-t border-border/40 text-[11px] text-muted-foreground space-y-2">
                    <p>{t("welcome.ios_hint")}</p>
                    <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs h-8" onClick={copyLink} data-testid="welcome-copy-link">{copied ? <Check size={12} /> : <Copy size={12} />} {copied ? t("welcome.copied") : t("welcome.copy")}</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4" data-testid="guide-android">
                  <div>
                    <p className="font-heading font-bold text-sm mb-2">{t("welcome.chrome_android")}</p>
                    <Step n={1} icon={Download} text={t("welcome.and1")} />
                    <Step n={2} icon={MoreVertical} text={t("welcome.and2")} />
                    <Step n={3} icon={CheckCircle2} text={t("welcome.and3")} />
                  </div>
                  <div className="pt-3 border-t border-border/40">
                    <p className="font-heading font-bold text-sm mb-2">{t("welcome.samsung")}</p>
                    <Step n={1} icon={MoreVertical} text={t("welcome.sam1")} />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row gap-2 mt-2">
          <Link to="/book" className="flex-1">
            <Button variant={installed ? "default" : "outline"} className={`w-full rounded-full h-11 gap-2 ${installed ? "bg-coral hover:bg-coral-600 text-white" : ""}`} data-testid="welcome-book-btn"><CalendarDays size={16} /> {t("welcome.book")}</Button>
          </Link>
          {!user && (
            <Link to="/register" className="flex-1">
              <Button variant="ghost" className="w-full rounded-full h-11" data-testid="welcome-register-btn">{t("welcome.register")}</Button>
            </Link>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-6">{t("welcome.footer")}</p>
      </motion.div>
    </div>
  );
}

function Step({ n, icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3 mb-2">
      <div className="w-6 h-6 rounded-full bg-coral/15 text-coral text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</div>
      <Icon size={15} className="text-teal mt-1 shrink-0" />
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}
